/**
 * StandalonePanel — a pop-out workspace panel in its own browser window.
 *
 * Reads panel config from URL search params:
 *   ?command=<cmd>&autoRefresh=<secs>&terminalId=<id>&panelId=<id>
 *
 * No sidebar, no top-nav — just the terminal filling the full viewport.
 * Auth token lives in localStorage (same origin) so no extra auth step needed.
 */
import { useEffect, useRef, useState, useCallback, useMemo } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { ArrowUpRight } from 'lucide-react'
import { useMultiTerminalStore } from '@/hooks/useTerminal'
import type { TerminalLine } from '@/lib/types'

// ── All commands for autocomplete (mirrored from WorkspacePage) ───────────

const ALL_COMMANDS = [
  { cmd: '/quote', alias: '/q BQ QR', desc: 'Real-time quote' },
  { cmd: '/chart', alias: '/c GP GIP COMP', desc: 'Chart' },
  { cmd: '/profile', alias: '/p DES FA', desc: 'Company profile' },
  { cmd: '/earnings', alias: '/e ERN EA', desc: 'Earnings' },
  { cmd: '/dividends', alias: '/div DVD', desc: 'Dividends' },
  { cmd: '/technicals', alias: 'TA', desc: 'Technical analysis' },
  { cmd: '/compare', alias: null, desc: 'Compare stocks' },
  { cmd: '/options', alias: '/opt', desc: 'Options chain' },
  { cmd: '/wacc', alias: null, desc: 'WACC' },
  { cmd: '/dcf', alias: null, desc: 'DCF valuation' },
  { cmd: '/ddm', alias: null, desc: 'Dividend discount' },
  { cmd: '/financials', alias: 'FA', desc: 'Financials' },
  { cmd: '/peers', alias: null, desc: 'Peer comparison' },
  { cmd: '/targets', alias: 'TGT', desc: 'Price targets' },
  { cmd: '/ratings', alias: 'RATD', desc: 'Analyst ratings' },
  { cmd: '/indices', alias: '/idx WB INDU NKY DAX', desc: 'Market indices' },
  { cmd: '/movers', alias: 'MOV', desc: 'Top movers' },
  { cmd: '/sectors', alias: 'S5SECT', desc: 'Sector performance' },
  { cmd: '/bonds', alias: 'GB', desc: 'Treasury yields' },
  { cmd: '/vix', alias: null, desc: 'VIX index' },
  { cmd: '/forex', alias: '/fx FXIP FXC', desc: 'Forex rates' },
  { cmd: '/crncy', alias: 'FXCM', desc: 'FX matrix' },
  { cmd: '/commodities', alias: 'CMD CMDTY', desc: 'Commodities' },
  { cmd: '/country', alias: null, desc: 'Country dashboard' },
  { cmd: '/crypto', alias: 'CTOP', desc: 'Crypto' },
  { cmd: '/topvol', alias: null, desc: 'Top volume' },
  { cmd: '/insider', alias: 'ANR BI', desc: 'Insider trades' },
  { cmd: '/congress', alias: null, desc: 'Congressional trades' },
  { cmd: '/guru', alias: null, desc: 'Guru portfolios' },
  { cmd: '/shorts', alias: 'SI MOST', desc: 'Short interest' },
  { cmd: '/squeeze', alias: null, desc: 'Squeeze candidates' },
  { cmd: '/sentiment', alias: 'SALT', desc: 'Social sentiment' },
  { cmd: '/wsb', alias: null, desc: 'WSB trending' },
  { cmd: '/fear', alias: null, desc: 'Fear & Greed' },
  { cmd: '/flows', alias: null, desc: 'ETF flows' },
  { cmd: '/contracts', alias: null, desc: 'Gov contracts' },
  { cmd: '/patents', alias: null, desc: 'Patents' },
  { cmd: '/lobbying', alias: null, desc: 'Lobbying' },
  { cmd: '/cacs', alias: 'CACS', desc: 'Corp. actions' },
  { cmd: '/spread', alias: null, desc: 'Bid/ask spread' },
  { cmd: '/float', alias: null, desc: 'Float & ownership' },
  { cmd: '/options-flow', alias: '/oflow OMON OV', desc: 'Options flow' },
  { cmd: '/pcr', alias: null, desc: 'Put/call ratio' },
  { cmd: '/maxpain', alias: null, desc: 'Max pain' },
  { cmd: '/portfolio', alias: '/pf PORT', desc: 'Portfolio' },
  { cmd: '/positions', alias: '/pos PMON', desc: 'Positions' },
  { cmd: '/risk', alias: 'MARS VAR', desc: 'Risk dashboard' },
  { cmd: '/stress', alias: null, desc: 'Stress test' },
  { cmd: '/pot', alias: null, desc: 'Disc. pot' },
  { cmd: '/pnl', alias: null, desc: 'P&L summary' },
  { cmd: '/goals', alias: null, desc: 'Goals' },
  { cmd: '/watchlist', alias: '/wl', desc: 'Watchlist' },
  { cmd: '/tra', alias: null, desc: 'Trade analysis' },
  { cmd: '/analyze', alias: '/run', desc: 'Agent analysis' },
  { cmd: '/recommendations', alias: '/recs', desc: 'Recommendations' },
  { cmd: '/consensus', alias: null, desc: 'Consensus' },
  { cmd: '/ftse100', alias: 'UKX', desc: 'FTSE 100 dashboard' },
  { cmd: '/sp500', alias: 'SPX', desc: 'S&P 500 dashboard' },
  { cmd: '/nasdaq', alias: null, desc: 'NASDAQ dashboard' },
  { cmd: '/sector', alias: null, desc: 'Sector deep-dive' },
  { cmd: '/macro', alias: 'ECO ECFC WEI', desc: 'Macro dashboard' },
  { cmd: '/pd', alias: null, desc: 'Portfolio dashboard' },
  { cmd: '/screener', alias: 'GRAB EQS SECF RV', desc: 'Screener' },
  { cmd: '/scan', alias: null, desc: 'Quick scan preset' },
  { cmd: '/news', alias: 'TOP N NI CN', desc: 'Headlines' },
  { cmd: '/calendar', alias: 'EVTS', desc: 'Events calendar' },
  { cmd: '/ipo', alias: null, desc: 'Upcoming IPOs' },
  { cmd: '/help', alias: 'HELP', desc: 'Help' },
  { cmd: '/status', alias: null, desc: 'Status' },
  { cmd: '/clear', alias: null, desc: 'Clear' },
  { cmd: '/des', alias: null, desc: '-> /profile' },
  { cmd: '/ta', alias: null, desc: '-> /technicals' },
  { cmd: '/mov', alias: null, desc: '-> /movers' },
  { cmd: '/gb', alias: null, desc: '-> /bonds' },
  { cmd: '/bq', alias: null, desc: '-> /quote' },
]

const REFRESH_OPTIONS = [
  { label: 'Off', value: 0 },
  { label: '30s', value: 30 },
  { label: '1m', value: 60 },
  { label: '5m', value: 300 },
]

// ── Standalone terminal (self-contained, no Zustand workspace store) ───────

export default function StandalonePanel() {
  const [searchParams] = useSearchParams()

  const initialCommand = searchParams.get('command') || ''
  const initialRefresh = Number(searchParams.get('autoRefresh') ?? 0)
  // Use a stable terminal ID derived from the URL param, or generate one
  const terminalId = useMemo(
    () => searchParams.get('terminalId') || `standalone-${Date.now()}`,
    [], // eslint-disable-line react-hooks/exhaustive-deps
  )
  const panelId = searchParams.get('panelId') || terminalId

  const [input, setInput] = useState('')
  const [historyIndex, setHistoryIndex] = useState(-1)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [selectedSuggestion, setSelectedSuggestion] = useState(0)
  const [autoRefresh, setAutoRefresh] = useState(initialRefresh)
  const [lastCommand, setLastCommand] = useState(initialCommand)

  const inputRef = useRef<HTMLInputElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const initDone = useRef(false)
  const autoRefreshRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const { terminals, createTerminal, executeCommand, clearTerminal } = useMultiTerminalStore()
  const terminal = terminals[terminalId]
  const history = terminal?.history ?? []
  const commandHistory = terminal?.commandHistory ?? []
  const executing = terminal?.executing ?? false

  // Set window title to the command for taskbar identification
  useEffect(() => {
    document.title = initialCommand
      ? `${initialCommand.toUpperCase()} — FinanceGenie`
      : 'Panel — FinanceGenie'
  }, [initialCommand])

  // Create terminal and run initial command
  useEffect(() => {
    createTerminal(terminalId)
    if (!initDone.current && initialCommand) {
      initDone.current = true
      setTimeout(() => {
        executeCommand(terminalId, initialCommand)
      }, 200)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh
  useEffect(() => {
    if (autoRefreshRef.current) {
      clearInterval(autoRefreshRef.current)
      autoRefreshRef.current = null
    }
    if (autoRefresh > 0 && lastCommand) {
      autoRefreshRef.current = setInterval(() => {
        executeCommand(terminalId, lastCommand)
      }, autoRefresh * 1000)
    }
    return () => {
      if (autoRefreshRef.current) clearInterval(autoRefreshRef.current)
    }
  }, [autoRefresh, lastCommand, terminalId, executeCommand])

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
    }
  }, [history])

  // BroadcastChannel: close this window if the parent removes the panel
  useEffect(() => {
    const channel = new BroadcastChannel('workspace-sync')
    channel.onmessage = (event) => {
      if (event.data?.type === 'panel-removed' && event.data.panelId === panelId) {
        window.close()
      }
    }
    return () => channel.close()
  }, [panelId])

  // Autocomplete
  const suggestions = useMemo(() => {
    if (!input.startsWith('/') || input.includes(' ')) return []
    const query = input.toLowerCase()
    return ALL_COMMANDS.filter(
      c =>
        c.cmd.startsWith(query) ||
        (c.alias &&
          c.alias
            .split(' ')
            .some(
              a =>
                a.toLowerCase().startsWith(query) ||
                ('/' + a.toLowerCase()).startsWith(query),
            )),
    ).slice(0, 6)
  }, [input])

  useEffect(() => {
    setShowSuggestions(suggestions.length > 0 && input.length > 0)
    setSelectedSuggestion(0)
  }, [suggestions, input])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.ctrlKey && e.key === 'l') {
        e.preventDefault()
        clearTerminal(terminalId)
        return
      }

      if (e.key === 'Enter') {
        e.preventDefault()
        if (input.trim()) {
          const cmd = input.trim()
          executeCommand(terminalId, cmd)
          setLastCommand(cmd)
          setInput('')
          setHistoryIndex(-1)
          setShowSuggestions(false)
        }
        return
      }

      if (e.key === 'Tab') {
        e.preventDefault()
        if (suggestions.length > 0) {
          setInput(suggestions[selectedSuggestion].cmd + ' ')
          setShowSuggestions(false)
        }
        return
      }

      if (showSuggestions && suggestions.length > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault()
          setSelectedSuggestion(prev => Math.min(prev + 1, suggestions.length - 1))
          return
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault()
          setSelectedSuggestion(prev => Math.max(prev - 1, 0))
          return
        }
      }

      if (e.key === 'ArrowUp' && !showSuggestions) {
        e.preventDefault()
        if (commandHistory.length === 0) return
        const newIndex =
          historyIndex === -1 ? commandHistory.length - 1 : Math.max(0, historyIndex - 1)
        setHistoryIndex(newIndex)
        setInput(commandHistory[newIndex])
        return
      }
      if (e.key === 'ArrowDown' && !showSuggestions) {
        e.preventDefault()
        if (historyIndex === -1) return
        const newIndex = historyIndex + 1
        if (newIndex >= commandHistory.length) {
          setHistoryIndex(-1)
          setInput('')
        } else {
          setHistoryIndex(newIndex)
          setInput(commandHistory[newIndex])
        }
        return
      }

      if (e.key === 'Escape') {
        setShowSuggestions(false)
        return
      }
    },
    [
      input,
      showSuggestions,
      suggestions,
      selectedSuggestion,
      commandHistory,
      historyIndex,
      executeCommand,
      clearTerminal,
      terminalId,
    ],
  )

  const lineClass = (line: TerminalLine) => {
    switch (line.type) {
      case 'command':   return 'text-crt-bright'
      case 'error':     return 'text-red-400'
      case 'system':    return 'text-crt-dim'
      case 'quote':     return 'text-crt-amber'
      case 'chart':     return 'text-crt-cyan'
      case 'dashboard': return 'text-crt-green'
      case 'table':     return 'text-crt-green'
      default:          return 'text-crt-green'
    }
  }

  return (
    <div
      className="flex flex-col h-screen bg-black overflow-hidden crt-panel-glow"
      onClick={() => inputRef.current?.focus()}
    >
      {/* CRT scanlines overlay */}
      <div className="absolute inset-0 pointer-events-none crt-scanlines opacity-20" aria-hidden />

      {/* Title bar */}
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-crt-green/30 bg-black/90 shrink-0 z-10">
        <div className="flex items-center gap-2">
          <span className="text-crt-green font-mono text-xs font-bold tracking-wider">
            FINANCEGENIE
          </span>
          <span className="text-crt-dim font-mono text-xs">/</span>
          <span className="text-crt-bright font-mono text-xs font-bold truncate max-w-[300px]">
            {lastCommand || 'PANEL'}
          </span>
          {executing && (
            <span className="text-crt-amber font-mono text-[10px] animate-pulse">EXEC</span>
          )}
        </div>

        <div className="flex items-center gap-3">
          {/* Auto-refresh */}
          <select
            value={autoRefresh}
            onChange={(e) => setAutoRefresh(Number(e.target.value))}
            className="bg-transparent text-crt-dim font-mono text-[10px] border border-crt-green/20
                       rounded px-1 py-0 outline-none cursor-pointer hover:border-crt-green/40"
            title="Auto-refresh interval"
          >
            {REFRESH_OPTIONS.map(o => (
              <option key={o.value} value={o.value} className="bg-black">
                {o.label}
              </option>
            ))}
          </select>

          {/* Return to workspace link */}
          <Link
            to="/app/workspace"
            className="flex items-center gap-1 text-crt-dim hover:text-crt-green font-mono text-[10px]
                       border border-crt-green/20 hover:border-crt-green/40 rounded px-2 py-0.5
                       transition-colors"
            title="Return to workspace"
          >
            <ArrowUpRight size={10} />
            WORKSPACE
          </Link>
        </div>
      </div>

      {/* Terminal output */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-3 py-2 font-mono text-xs leading-relaxed crt-scrollbar min-h-0"
        role="log"
      >
        {history.map(line => (
          <div key={line.id} className="py-px">
            {line.type === 'command' ? (
              <div className="flex gap-1">
                <span className="text-crt-dim shrink-0">[{line.timestamp}]</span>
                <span className="text-crt-bright shrink-0">{'>'}</span>
                <span className="text-crt-bright">{line.content}</span>
              </div>
            ) : line.type === 'system' ? (
              <div className="flex gap-1">
                <span className="text-crt-dim shrink-0">SYS</span>
                <span className="text-crt-dim whitespace-pre">{line.content}</span>
              </div>
            ) : (
              <div className={`whitespace-pre ${lineClass(line)}`}>{line.content}</div>
            )}
          </div>
        ))}
      </div>

      {/* Autocomplete suggestions */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="px-3 pb-0.5 border-t border-crt-green/10 shrink-0 z-10">
          <div className="font-mono text-[10px] py-0.5 flex flex-wrap gap-x-2 gap-y-0">
            {suggestions.map((s, i) => (
              <button
                key={s.cmd}
                type="button"
                onClick={() => {
                  setInput(s.cmd + ' ')
                  setShowSuggestions(false)
                  inputRef.current?.focus()
                }}
                className={`px-0.5 rounded transition-colors ${
                  i === selectedSuggestion
                    ? 'text-crt-bright bg-crt-green/20'
                    : 'text-crt-dim hover:text-crt-green'
                }`}
              >
                {s.cmd}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Command input */}
      <div className="flex items-center gap-1 px-3 py-2 border-t border-crt-green/20 bg-black/50 shrink-0 z-10">
        <span className="text-crt-green font-mono text-xs font-bold shrink-0">
          {executing ? '...' : '>'}
        </span>
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={e => {
            setInput(e.target.value)
            setHistoryIndex(-1)
          }}
          onKeyDown={handleKeyDown}
          placeholder={executing ? 'executing...' : '/command'}
          disabled={executing}
          spellCheck={false}
          autoComplete="off"
          autoFocus
          className="flex-1 bg-transparent font-mono text-xs text-crt-green placeholder-crt-dim/50
                     border-none outline-none caret-crt-green disabled:opacity-50 min-w-0"
          aria-label="Terminal command input"
        />
      </div>
    </div>
  )
}
